import { useEffect, useState } from 'react';

import { CheckCodeEntityType, typicodeApi } from 'shared/api';

import { useDebounce } from './useDebounce';

type CodeCheckStatus = 'idle' | 'checking' | 'unique' | 'duplicate';

export const useCheckCodeUnique = (
  entityType: CheckCodeEntityType,
  code: string,
  excludeId?: string,
  enabled: boolean = true,
) => {
  const [status, setStatus] = useState<CodeCheckStatus>('idle');
  const debouncedCode = useDebounce(code, 250);

  useEffect(() => {
    if (!enabled || !debouncedCode || debouncedCode.trim().length === 0) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    setStatus('checking');

    typicodeApi.checkCode
      .checkCode({ entityType, code: debouncedCode, excludeId })
      .then(({ data }) => {
        if (!cancelled) {
          setStatus(data.exists ? 'duplicate' : 'unique');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('idle');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedCode, entityType, excludeId, enabled]);

  const reset = () => setStatus('idle');

  return {
    status,
    isChecking: status === 'checking',
    isDuplicate: status === 'duplicate',
    isUnique: status === 'unique',
    reset,
  };
};
